<template>
    <div class="ph-dashboard">
        <div class="ph-dashboard__top-section">
            <div class="ph-dashboard__top-img-wrapper">
                <img src="~@/assets/images/ph-banner.png" alt="kasanowa" />
            </div>
            <div class="ph-dashboard__top-box-wrapper">
                <div class="ph-dashboard__top-title-wrapper">{{ $t('dashboard.yourMoney') }}</div>
                <div class="ph-dashboard__money">{{ userInfo?.wallet?.money || '0.00' }} PLN</div>
                <div class="ph-dashboard__text">{{ $t('dashboard.updatedYesterday') }}</div>
                <div class="ph-dashboard__text">{{ $t('dashboard.summaryYouEarned') }} <span>{{ userSumEarnedMoney() }} PLN,</span> </div>
                <div class="ph-dashboard__text"><span>{{ userInfo?.money_withdrawed || '0.00' }} PLN</span> {{ $t('dashboard.alreadyWithdraw') }}.</div>
                <div v-if="isPh" class="ph-dashboard__btn-wrapper">
                    <KButton
                        id="withdraw"
                        :label="$t('dashboard.withdraw')"
                        icon="arrowRight"
                        type="submit"
                        :color="black"
                        uppercase
                        :loading="loading"
                        @click="openDialog"
                    />
                </div>
            </div>
        </div>

        <KDialog
            :is-open-dialog-action="isOpenDialog"
            :is-close-dialog-action="isCloseDialog"
            close-header-btn
            @close-dialog="closeDialogWhenNoTargetPressed"
        >
            <template v-slot:default>
                <div>
                    <div class="ph-dashboard__dialog-title">{{ $t('salesRep.withdrawDialogInfo') }}</div>
                    <div class="ph-dashboard__dialog-description">{{ $t('salesRep.withdrawAllMoney') }}</div>
                    <div class="ph-dashboard__btn-dialog-wrapper">
                        <KButton
                            id="cancel-card"
                            :label="$t('salesRep.cancel')"
                            secondary
                            @click="closeDialogWhenNoTargetPressed"
                        />
                        <KButton
                            id="withdraw-action"
                            :label="$t('salesRep.withdraw')"
                            type="submit"
                            secondary
                            :loading="loading"
                            @click="onSubmit"
                        />
                    </div>
                </div>
            </template>
        </KDialog>
    </div>
</template>

<script setup lang="ts">
import { ref } from '@vue/runtime-core';
import { useUserStore } from '@/stores/user';
import { storeToRefs } from 'pinia';
import { toast } from 'vue3-toastify';
import WithdrawApi from '@/api/v1/WithdrawApi';
import UserApi from '@/api/v1/UserApi';
import variables from '@/assets/scss/modules/variables.module.scss';
import KButton from '@/components/atoms/KButton.vue';
import KDialog from '@/components/atoms/KDialog.vue';

const { userInfo, isPh } = storeToRefs(useUserStore());
const { black } = variables;

const isOpenDialog = ref<boolean>(false);
const isCloseDialog = ref<boolean>(true);
const closeDialogWhenNoTargetPressed = () => {
    isOpenDialog.value = false;
    isCloseDialog.value = true;
};
const openDialog = () => {
    isOpenDialog.value = true;
    isCloseDialog.value = false;
};

const { setUserInfo } = useUserStore();
const userApi = new UserApi();
const loading = ref<boolean>(false);
const user = async() => {
    loading.value = true;
    try {
        const result = await userApi.user();
        if (!result.isSuccess) {
            toast.error(t('auth.toast.errorGetUser'));
            loading.value = false;
            return;
        }
        await setUserInfo(result.payload);
    } catch (e) {
        toast.error(t('auth.toast.errorGetUser'));
        loading.value = false;
        return;
    }
    loading.value = false;
};
const withdrawApi = new WithdrawApi();
const onSubmit = async() => {
    loading.value = true;
    if (null == userInfo.value && null == userInfo.value.wallet || userInfo.value.wallet.money == null || +userInfo.value.wallet.money === 0) {
        toast.error(t('salesRep.toast.errorWithdraw'));
        loading.value = false;
        return;
    }
    try {
        const result = await withdrawApi.withdrawMoney({
            amount: +userInfo.value.wallet.money,
        });
        if (!result.isSuccess) {
            toast.error(t('salesRep.toast.errorWithdraw'));
            loading.value = false;
            return;
        }
        await user();
        await closeDialogWhenNoTargetPressed();
    } catch (e) {
        toast.error(t('salesRep.toast.errorWithdraw'));
        loading.value = false;
        return;
    }
    loading.value = false;
};

const userSumEarnedMoney = (): number | string => {
    if (userInfo.value == null) return 0;
    if (userInfo.value.money_earned == null && null == userInfo.value.money_withdrawed) {
        return 0;
    }
    if (userInfo.value.money_earned == null && null != userInfo.value.money_withdrawed) {
        return userInfo.value.money_withdrawed;
    }
    if (userInfo.value.money_earned != null && null == userInfo.value.money_withdrawed) {
        return userInfo.value.money_earned;
    }
    return userInfo.value.money_earned + userInfo.value.money_withdrawed;
};
</script>

<style scoped lang="scss">
.ph-dashboard {
    &__top-section {
        margin-top: 140px;
        display: flex;
        gap: 50px;
        flex-direction: column;
        align-items: center;

        @include media-breakpoint-up(lg) {
            flex-direction: row-reverse;
            gap: 0;
        }

        img {
            width: 100%;
            height: 100%;
            max-width: 345px;
            max-height: 150px;
        }
    }

    &__top-img-wrapper {
        width: 100%;
        height: 100%;
        display: flex;
        align-items: center;
        justify-content: center;

        @include media-breakpoint-up(lg) {
            width: 50%;
        }
    }

    &__top-box-wrapper {
        position: relative;
        width: 100%;
        height: 100%;
        max-width: 555px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        border-radius: 10px;
        border: 1px solid $primary-400;
        background: rgba($primary-400, 0.6);
        backdrop-filter: blur(2px) saturate(150%);
        -webkit-backdrop-filter: blur(5px) saturate(150%);
        padding: 36px 16px;

        @include media-breakpoint-up(lg) {
            width: 50%;
            padding: 50px 16px;
        }
    }

    &__money {
        background-color: $black;
        font-size: 32px;
        color: $secondary-400;
        padding: 2px 10px;
        font-weight: 900;
        letter-spacing: 0;
        margin-bottom: 15px;

        @include media-breakpoint-up(md) {
            font-size: 60px;
            padding: 2px 20px;
        }

        @include media-breakpoint-up(xl) {
            font-size: 72px;
            padding: 2px 20px;
        }
    }

    &__text {
        color: $black;
        text-align: center;

        span {
            font-weight: 700;
        }
    }

    &__top-title-wrapper {
        position: absolute;
        top: -40px;
        left: 50%;
        transform: translateX(-50%);
        width: max-content;
        background-color: $primary-400;
        font-weight: 900;
        color: $black;
        font-size: 35px;
        padding: 0 14px;
        letter-spacing: 0;
        text-transform: uppercase;

        @include media-breakpoint-up(lg) {
            background-color: $white;
            font-size: 52px;
            padding: 0 55px;
            top: -40px;
        }
    }

    &__btn-wrapper {
        position: absolute;
        bottom: -20px;
        left: 50%;
        transform: translateX(-50%);
    }

    &__btn-dialog-wrapper {
        margin-top: 10px;
        display: flex;
        flex-direction: column;
        gap: 10px;

        @include media-breakpoint-up(sm) {
            margin-top: 20px;
            flex-direction: row;
            width: 100%;
            justify-content: space-around;
        }
    }

    &__dialog-title {
        font-size: 18px;
        letter-spacing: 0;
    }

    &__dialog-description {
        margin-top: 20px;
        font-size: 14px;
        letter-spacing: 0;
    }
}
</style>
